import { useCallback, useEffect, useState } from 'react';
import { useStyletron } from 'baseui';
import { Overflow } from 'baseui/icon';
import { toaster } from 'baseui/toast';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { LayoutContent } from 'components/Common/Layout';
import usePaginationHook from 'components/UI/ENGTPagination/PaginationHook';
import ENGTTable, { ITableColumnProps } from 'components/UI/ENGTTable/ENGTTable';
import ENGTToasterContainer from 'components/UI/ENGTToaster/ENGTToasterContainer';
import NoDataTemplate from 'components/UI/NoDataTemplate/NoDataTemplate';

import { IObjProps } from 'shared/consts/types';
import { VOICE_CAMPAIGN_EXPORT_TYPE, VOICE_CAMPAIGN_STATUS } from 'shared/enum';
import { isValidResponseObject, validateFileName } from 'shared/helpers';
import { getChannelTooltip } from 'shared/helpers/broadcastHelpers';
import { getVoiceCampaignDisplayStatus } from 'shared/helpers/campaignHelpers';
import NoCampaign from 'shared/icons/Broadcast/NoCampaign.svg';

import { exportVoiceCampaignAction } from 'store/Campaign/actions';
import { API as CampaignAPI } from 'store/Campaign/api';
import { userId } from 'store/Users/selectors';

import { ThreeColumnRouteLinks } from 'router/links/ThreeColumnRouteLinks';

import RetargetVoiceCampaignModal from './components/RetargetVoiceCampaignModal';
import StopVoiceCampaignModal from './components/StopVoiceCampaignModal';
import VoiceCampaignStatus from './components/VoiceCampaignStatus';
import { RetargetOutcome, VoiceCampaignMedium, VoiceCampaignSort } from './constants';
import getVoiceCampaignChannelKey, { isOriginalVoiceCampaign } from './helpers';

interface IVoiceCampaignRow {
	agentId?: string;
	campaignId: string;
	channel?: string;
	medium?: string;
	name?: string;
	parentCampaignId?: string | null;
	status?: string;
	statusReason?: string;
	targetGroup?: string;
	totalUserCount: number;
	attemptedCount?: number;
	connectedCount?: number;
	notConnectedCount?: number;
	optedOutUserCount?: number;
	triggerStartAt?: string;
	type?: string;
}

const getDefaultVoiceCampaignStatusLabel = (status: string) =>
	status
		?.toLowerCase()
		.replace(/_/g, ' ')
		.replace(/\b\w/g, (character) => character.toUpperCase()) || '-';

const withRowData =
	(processor: (rowData: IVoiceCampaignRow) => React.ReactNode | string | number) =>
	(_value: unknown, rowData: IVoiceCampaignRow) =>
		processor(rowData);

const isVoiceCampaignGatewaySuccess = (response: IObjProps) =>
	response && response.status === 200 && response.data?.status?.code === 1000;

const STOPPABLE_VOICE_CAMPAIGN_STATUSES = [
	VOICE_CAMPAIGN_STATUS.SCHEDULED,
	VOICE_CAMPAIGN_STATUS.WAITING,
	VOICE_CAMPAIGN_STATUS.RUNNING,
	VOICE_CAMPAIGN_STATUS.PAUSED,
];

const VoiceCampaigns = () => {
	const [css, theme]: [any, any] = useStyletron();
	const { t } = useTranslation(['common', 'errors', 'pages']);
	const navigate = useNavigate();
	const dispatch = useDispatch<any>();
	const uid = useSelector(userId);
	const [data, setData] = useState<Array<IVoiceCampaignRow>>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [processingCampaignIds, setProcessingCampaignIds] = useState<Array<string>>([]);
	const [selectedCampaignToStop, setSelectedCampaignToStop] = useState<IVoiceCampaignRow | null>(null);
	const [selectedCampaignToRetarget, setSelectedCampaignToRetarget] = useState<IVoiceCampaignRow | null>(null);

	const paginationMethods = usePaginationHook({
		onChange: (page: number | undefined, size: number | undefined) => fetchCampaigns(page, size),
	});

	const { pagination, setPagination } = paginationMethods;
	const { size, page } = pagination;

	const fetchCampaigns = useCallback(
		(currentPage = page, currentSize = size) => {
			setIsLoading(true);
			CampaignAPI.getVoiceCampaignHistory({
				medium: VoiceCampaignMedium.VOICE,
				page: (currentPage || 1) - 1,
				size: currentSize,
				sort: VoiceCampaignSort.TRIGGER_START_AT_DESC,
			}).then(
				(res: IObjProps) => {
					if (isValidResponseObject(res)) {
						const responseObj = res.data.responseObject;
						setData(responseObj.content || []);
						setPagination({
							size: currentSize,
							page: currentPage,
							totalPages: responseObj.totalPages || 0,
							totalElements: responseObj.totalElements || 0,
						});
					} else {
						setData([]);
					}
					setIsLoading(false);
				},
				() => {
					setData([]);
					setIsLoading(false);
				}
			);
		},
		[page, setPagination, size]
	);

	useEffect(() => {
		fetchCampaigns();
	}, [fetchCampaigns]);

	const setRowProcessing = (campaignId: string, isProcessing: boolean) => {
		setProcessingCampaignIds((current) =>
			isProcessing ? Array.from(new Set([...current, campaignId])) : current.filter((id) => id !== campaignId)
		);
	};

	const isRowProcessing = (campaignId: string) => processingCampaignIds.includes(campaignId);

	const showNegativeToaster = (description: string) =>
		toaster.negative(<ENGTToasterContainer title={t('common:error')} description={description} />, {});

	const showPositiveToaster = (description: string) =>
		toaster.positive(<ENGTToasterContainer title={t('common:success')} description={description} />, {});

	const handleExportCampaign = (rowData: IVoiceCampaignRow) => {
		setRowProcessing(rowData.campaignId, true);
		const fileName = `${validateFileName(`${rowData.name || 'Campaign'}_${VOICE_CAMPAIGN_EXPORT_TYPE.TOTAL}`)}.csv`;
		const params: IObjProps = {
			campaignId: rowData.campaignId,
			userId: uid,
			type: VOICE_CAMPAIGN_EXPORT_TYPE.TOTAL,
		};

		dispatch(exportVoiceCampaignAction(params, fileName))
			.then(() => {
				showPositiveToaster(t('pages:voiceCampaign.toaster.exportInitiatedDesc'));
			})
			.catch(() => {
				showNegativeToaster(t('common:somethingWentWrong'));
			})
			.finally(() => setRowProcessing(rowData.campaignId, false));
	};

	const handlePauseCampaign = (rowData: IVoiceCampaignRow) => {
		setRowProcessing(rowData.campaignId, true);
		CampaignAPI.pauseVoiceCampaign(rowData.campaignId).then(
			(response: IObjProps) => {
				if (isVoiceCampaignGatewaySuccess(response)) {
					showPositiveToaster(t('pages:voiceCampaign.toaster.pauseSuccess'));
					fetchCampaigns();
				} else {
					showNegativeToaster(t('common:somethingWentWrong'));
				}
				setRowProcessing(rowData.campaignId, false);
			},
			() => {
				showNegativeToaster(t('common:somethingWentWrong'));
				setRowProcessing(rowData.campaignId, false);
			}
		);
	};

	const handleResumeCampaign = (rowData: IVoiceCampaignRow) => {
		setRowProcessing(rowData.campaignId, true);
		CampaignAPI.resumeVoiceCampaign(rowData.campaignId).then(
			(response: IObjProps) => {
				if (isVoiceCampaignGatewaySuccess(response)) {
					showPositiveToaster(t('pages:voiceCampaign.toaster.resumeSuccess'));
					fetchCampaigns();
				} else {
					showNegativeToaster(t('common:somethingWentWrong'));
				}
				setRowProcessing(rowData.campaignId, false);
			},
			() => {
				showNegativeToaster(t('common:somethingWentWrong'));
				setRowProcessing(rowData.campaignId, false);
			}
		);
	};

	const handleStopCampaign = (rowData: IVoiceCampaignRow) => {
		setSelectedCampaignToStop(rowData);
	};

	const continueRetargetCampaign = ({
		outcomes,
		dispositions,
	}: {
		outcomes: RetargetOutcome[];
		dispositions: string[];
	}) => {
		if (!selectedCampaignToRetarget?.campaignId) {
			return;
		}
		const params = new URLSearchParams({
			retargetParentId: selectedCampaignToRetarget.campaignId,
			outcomes: outcomes.join(','),
			...(dispositions.length ? { dispositions: dispositions.join(',') } : {}),
		});
		setSelectedCampaignToRetarget(null);
		navigate(`${ThreeColumnRouteLinks.createVoiceCampaign}?${params.toString()}`);
	};

	const confirmStopCampaign = () => {
		if (!selectedCampaignToStop?.campaignId) {
			return;
		}

		const { campaignId } = selectedCampaignToStop;

		setRowProcessing(campaignId, true);
		CampaignAPI.stopVoiceCampaign(campaignId).then(
			(response: IObjProps) => {
				if (isVoiceCampaignGatewaySuccess(response)) {
					showPositiveToaster(t('pages:voiceCampaign.toaster.stopSuccess'));
					fetchCampaigns();
				} else {
					showNegativeToaster(t('common:somethingWentWrong'));
				}
				setSelectedCampaignToStop(null);
				setRowProcessing(campaignId, false);
			},
			() => {
				showNegativeToaster(t('common:somethingWentWrong'));
				setSelectedCampaignToStop(null);
				setRowProcessing(campaignId, false);
			}
		);
	};

	const renderCampaignName = (rowData: IVoiceCampaignRow) => (
		<div
			className={css({
				textDecoration: 'underline',
				cursor: 'pointer',
				color: theme.colors.primaryA,
			})}
			key={rowData.campaignId}
			onClick={() =>
				navigate(
					`${ThreeColumnRouteLinks.voiceCampaignAnalyticsSelected}?id=${encodeURIComponent(rowData.campaignId)}`
				)
			}
		>
			{rowData.name || '-'}
		</div>
	);

	const tableColumns: Array<ITableColumnProps> = [
		{
			id: 'campaignName',
			name: t('pages:voiceCampaign.analytics.campaignName'),
			span: 2,
			processor: withRowData(renderCampaignName),
		},
		{
			id: 'triggerStartAt',
			name: t('pages:voiceCampaign.analytics.publishTime'),
			span: 2,
			processor: withRowData((rowData: IVoiceCampaignRow) =>
				rowData.triggerStartAt ? t('common:standardDateTimeFormatter', { date: rowData.triggerStartAt }) : '-'
			),
		},
		{
			id: 'totalCount',
			name: t('pages:voiceCampaign.analytics.targetUsers'),
			span: 1.3,
			processor: withRowData((rowData: IVoiceCampaignRow) => rowData.totalUserCount),
		},
		{
			id: 'attemptedCount',
			name: t('pages:voiceCampaign.analytics.attempted'),
			span: 1.3,
			processor: withRowData((rowData: IVoiceCampaignRow) => rowData.attemptedCount ?? '-'),
		},
		{
			id: 'connectedCount',
			name: t('pages:voiceCampaign.analytics.connected'),
			span: 1.3,
			processor: withRowData((rowData: IVoiceCampaignRow) => rowData.connectedCount ?? '-'),
		},
		{
			id: 'notConnectedCount',
			name: t('pages:voiceCampaign.analytics.notConnected'),
			span: 1.3,
			processor: withRowData((rowData: IVoiceCampaignRow) => rowData.notConnectedCount ?? '-'),
		},
		{
			id: 'channel',
			name: t('pages:voiceCampaign.analytics.channels'),
			span: 1.2,
			processor: withRowData((rowData: IVoiceCampaignRow) => {
				const channelKey = getVoiceCampaignChannelKey(rowData.channel);

				return channelKey ? getChannelTooltip(channelKey, css) : '-';
			}),
		},
		{
			id: 'status',
			name: t('pages:voiceCampaign.analytics.status'),
			span: 1.2,
			processor: withRowData((rowData: IVoiceCampaignRow) => {
				const normalizedStatus = getVoiceCampaignDisplayStatus(rowData.status, rowData.statusReason);
				const localizedStatusLabel = t(`pages:voiceCampaign.status.${normalizedStatus}`, {
					defaultValue: getDefaultVoiceCampaignStatusLabel(normalizedStatus),
				});

				return (
					<VoiceCampaignStatus
						label={localizedStatusLabel}
						status={rowData.status || ''}
						statusReason={rowData.statusReason || ''}
					/>
				);
			}),
		},
	];

	const rowActions = (rowData: IVoiceCampaignRow) => {
		const displayStatus = getVoiceCampaignDisplayStatus(rowData.status, rowData.statusReason);

		return [
			{
				icon: <Overflow size={24} />,
				onClick: () => {},
				items: [
					{
						label: t('pages:voiceCampaign.analytics.viewCampaign'),
						visible: () => true,
						disable: () => false,
						onClick: () => {
							navigate(
								`${ThreeColumnRouteLinks.voiceCampaignAnalyticsSelected}?id=${encodeURIComponent(rowData.campaignId)}`
							);
						},
					},
					{
						label: t('pages:voiceCampaign.retarget.menuLabel'),
						visible: () => isOriginalVoiceCampaign(rowData),
						disable: () => isRowProcessing(rowData?.campaignId),
						onClick: () => setSelectedCampaignToRetarget(rowData),
					},
					{
						label: t('pages:voiceCampaign.analytics.exportAllData'),
						visible: () => true,
						disable: () => isRowProcessing(rowData?.campaignId) || !rowData.totalUserCount,
						onClick: () => handleExportCampaign(rowData),
					},
					{
						label: t('pages:voiceCampaign.analytics.pauseCampaign'),
						visible: () => true,
						disable: () =>
							isRowProcessing(rowData?.campaignId) || displayStatus !== VOICE_CAMPAIGN_STATUS.RUNNING,
						onClick: () => handlePauseCampaign(rowData),
					},
					{
						label: t('pages:voiceCampaign.analytics.stopCampaign'),
						visible: () => true,
						disable: () =>
							isRowProcessing(rowData?.campaignId) ||
							!STOPPABLE_VOICE_CAMPAIGN_STATUSES.includes(displayStatus as VOICE_CAMPAIGN_STATUS),
						onClick: () => handleStopCampaign(rowData),
					},
					{
						label: t('pages:voiceCampaign.analytics.resumeCampaign'),
						visible: () => true,
						disable: () =>
							isRowProcessing(rowData?.campaignId) || displayStatus !== VOICE_CAMPAIGN_STATUS.PAUSED,
						onClick: () => handleResumeCampaign(rowData),
					},
				],
			},
		];
	};

	if (!isLoading && data.length === 0) {
		return (
			<NoDataTemplate
				bannerImage={{
					imageUrl: NoCampaign,
					altText: t('pages:voiceCampaign.analytics.noCampaignsAlt'),
				}}
				heading={t('pages:voiceCampaign.analytics.noCampaigns')}
				contentCss={{
					maxWidth: '28rem',
					color: theme.colors.accent50,
					marginTop: '0.5rem',
				}}
			/>
		);
	}

	return (
		<>
			<LayoutContent>
				<ENGTTable
					columns={tableColumns}
					rows={data}
					pagination={paginationMethods}
					paginationType='footer'
					loader={isLoading}
					rowActions={rowActions}
					overrides={{
						wrapper: {
							marginLeft: '0rem',
							marginRight: '0rem',
							marginTop: '0rem',
							flexGrow: 1,
						},
					}}
				/>
			</LayoutContent>
			{selectedCampaignToStop && (
				<StopVoiceCampaignModal
					onConfirm={confirmStopCampaign}
					toggleStopModal={() => setSelectedCampaignToStop(null)}
					stopsChildCampaigns={isOriginalVoiceCampaign(selectedCampaignToStop)}
				/>
			)}
			{selectedCampaignToRetarget && (
				<RetargetVoiceCampaignModal
					onClose={() => setSelectedCampaignToRetarget(null)}
					onContinue={continueRetargetCampaign}
					sourceAgentId={selectedCampaignToRetarget.agentId}
					sourceCampaignId={selectedCampaignToRetarget.campaignId}
				/>
			)}
		</>
	);
};

export default VoiceCampaigns;
